import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { usuarioEmpresasService, UsuarioEmpresa } from '../src/services/usuarioEmpresasService';
import { usuariosService, Usuario } from '../src/services/usuariosService';
import { empresasService, Empresa } from '../src/services/empresasService';
import { rolesService, Rol } from '../src/services/rolesService';
import { useAuth } from '../src/contexts/AuthContext';

interface UsuarioEmpresasProps {
  onClose: () => void;
}

interface FormDataState {
  usuarioId: number;
  empresaId: number;
  rolId: number;
}

type SortableKey = 'nombreUsuario' | 'nombreEmpresa' | 'nombreRol';

const UsuarioEmpresas: React.FC<UsuarioEmpresasProps> = ({ onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.nombreRol === 'SuperAdmin';

  // Inject CSS styles for modal animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes disminuir {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(0.001); opacity: 0; }
      }
      @keyframes crecer {
        0% { transform: scale(0.001); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const [asignaciones, setAsignaciones] = useState<UsuarioEmpresa[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [roles, setRoles] = useState<Rol[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Default to user's empresa if not SuperAdmin
  const [selectedEmpresaFilter, setSelectedEmpresaFilter] = useState<number | null>(user?.empresaId ?? null);
  const [sortConfig, setSortConfig] = useState<{ key: SortableKey; direction: 'asc' | 'desc' } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [editingAsignacion, setEditingAsignacion] = useState<UsuarioEmpresa | null>(null);
  const [formData, setFormData] = useState<FormDataState>({ usuarioId: 0, empresaId: 0, rolId: 0 });
  const [isSaving, setIsSaving] = useState(false);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const [asignacionesData, usuariosData, empresasData, rolesData] = await Promise.all([
        usuarioEmpresasService.getAll(),
        usuariosService.getAll(),
        empresasService.getAll(),
        rolesService.getAll()
      ]);
      setAsignaciones(asignacionesData);
      setUsuarios(usuariosData);
      setEmpresas(empresasData);
      setRoles(rolesData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar datos';
      setErrorMessage(message);
      showErrorAlert(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Update selectedEmpresaFilter when user becomes available
  useEffect(() => {
    if (user?.empresaId && !isSuperAdmin) {
      setSelectedEmpresaFilter(user.empresaId);
    }
  }, [user?.empresaId, isSuperAdmin]);

  const filteredAsignaciones = useMemo(() => {
    const needle = searchTerm.toLowerCase();
    let filtered = asignaciones.filter((item) => {
      if (selectedEmpresaFilter && item.empresaId !== selectedEmpresaFilter) return false;
      return (
        item.nombreUsuario.toLowerCase().includes(needle) ||
        item.nombreEmpresa.toLowerCase().includes(needle) ||
        item.nombreRol.toLowerCase().includes(needle)
      );
    });

    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        const aValue = a[sortConfig.key].toLowerCase();
        const bValue = b[sortConfig.key].toLowerCase();
        if (aValue < bValue) return -1 * direction;
        if (aValue > bValue) return 1 * direction;
        return 0;
      });
    }

    return filtered;
  }, [asignaciones, searchTerm, selectedEmpresaFilter, sortConfig]);

  const filteredUsuariosForEmpresa = useMemo(() => {
    if (!formData.empresaId) return usuarios.filter(u => u.activo);
    return usuarios.filter((u) => u.empresaId === formData.empresaId && u.activo);
  }, [formData.empresaId, usuarios]);

  const showErrorAlert = (message: string) => {
    Swal.fire({
      title: 'Error',
      text: message,
      icon: 'error',
      background: 'rgba(15,23,42,0.85)',
      color: '#fff',
      confirmButtonColor: '#ef4444',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    });
  };

  const handleSort = (key: SortableKey) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const openCreateModal = () => {
    setEditingAsignacion(null);
    // For non-SuperAdmin, always use user's empresaId
    const defaultEmpresa = isSuperAdmin ? (selectedEmpresaFilter || empresas[0]?.empresaId || 0) : (user?.empresaId ?? 0);
    const usuariosForEmpresa = usuarios.filter(u => u.empresaId === defaultEmpresa && u.activo);
    setFormData({
      usuarioId: usuariosForEmpresa[0]?.usuarioId || usuarios[0]?.usuarioId || 0,
      empresaId: defaultEmpresa,
      rolId: roles.filter(r => r.activo)[0]?.rolId || 0
    });
    setIsModalOpen(true);
  };

  const openEditModal = (asignacion: UsuarioEmpresa) => {
    setEditingAsignacion(asignacion);
    setFormData({
      usuarioId: asignacion.usuarioId,
      empresaId: asignacion.empresaId,
      rolId: asignacion.rolId
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
      setEditingAsignacion(null);
    }, 300);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (formData.usuarioId === 0 || formData.empresaId === 0 || formData.rolId === 0) {
      showErrorAlert('Debe seleccionar Usuario, Empresa y Rol');
      return;
    }

    setIsSaving(true);

    try {
      if (editingAsignacion) {
        const updated = await usuarioEmpresasService.update(editingAsignacion.usuarioEmpresaId, formData);
        setAsignaciones((prev) => prev.map((item) => (item.usuarioEmpresaId === updated.usuarioEmpresaId ? updated : item)));
        await Swal.fire({
          title: 'Asignación actualizada',
          text: 'Los cambios se guardaron correctamente.',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          showConfirmButton: false,
          backdrop: false,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      } else {
        const created = await usuarioEmpresasService.create(formData);
        setAsignaciones((prev) => [created, ...prev]);
        await Swal.fire({
          title: 'Asignación creada',
          text: 'La relación usuario-empresa se registró correctamente.',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          showConfirmButton: false,
          backdrop: false,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      }
      closeModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo guardar la asignación.';
      showErrorAlert(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (asignacion: UsuarioEmpresa) => {
    Swal.fire({
      title: '¿Eliminar asignación?',
      html: `<strong>${asignacion.nombreUsuario}</strong> → ${asignacion.nombreEmpresa}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await usuarioEmpresasService.delete(asignacion.usuarioEmpresaId);
        setAsignaciones((prev) => prev.filter((item) => item.usuarioEmpresaId !== asignacion.usuarioEmpresaId));
        await Swal.fire({
          title: 'Asignación eliminada',
          text: 'Se eliminó la relación correctamente.',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          showConfirmButton: false,
          backdrop: false,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'No se pudo eliminar la asignación.';
        showErrorAlert(message);
      }
    });
  };

  // Auto-select first usuario when empresa changes in form
  useEffect(() => {
    if (!formData.empresaId) return;
    const available = usuarios.filter((u) => u.empresaId === formData.empresaId && u.activo);
    if (available.length === 0) return;
    if (available.some((u) => u.usuarioId === formData.usuarioId)) return;
    setFormData((prev) => ({ ...prev, usuarioId: available[0].usuarioId }));
  }, [formData.empresaId, formData.usuarioId, usuarios]);

  return (
    <div className="pt-4 fixed inset-0 z-50 flex items-start justify-center bg-white dark:bg-background-dark overflow-auto" style={{ animation: 'crecer 0.3s ease-out' }}>
      <div className="w-full max-w-6xl px-2 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-primary transition-colors">
              <span className="material-symbols-outlined">arrow_back</span>
              Volver
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-2xl">groups</span>
            <h2 className="text-2xl  text-secondary dark:text-white">Usuario - Rol - Empresa</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </button>
        </div>

        {/* Toolbar */}
        <div className="bg-[#307CAF] text-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {isSuperAdmin && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide mb-1">Empresa</label>
                    <select
                      value={selectedEmpresaFilter ?? ''}
                      onChange={(e) => setSelectedEmpresaFilter(e.target.value ? Number(e.target.value) : null)}
                      className="w-full rounded-full border border-transparent bg-white dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Todas</option>
                      {empresas.filter(e => e.activa).map((empresa) => (
                        <option key={empresa.empresaId} value={empresa.empresaId}>
                          {empresa.nombreEmpresa}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide mb-1">Buscar</label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                    <input
                      type="text"
                      placeholder="Buscar por usuario, empresa o rol..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-transparent rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span>{filteredAsignaciones.length} asignaciones</span>
              <button
                onClick={loadData}
                className="inline-flex items-center gap-1 px-3 py-1 border border-white/20 rounded-full hover:bg-white/10 transition"
                title="Recargar"
              >
                <span className="material-symbols-outlined text-base">refresh</span>
                Recargar
              </button>
              <button
                onClick={openCreateModal}
                disabled={usuarios.length === 0 || empresas.length === 0 || roles.length === 0}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#307CAF] shadow hover:bg-slate-50 transition-colors disabled:opacity-60"
              >
                <span className="material-symbols-outlined text-base">add</span>
                Nueva Asignación
              </button>
            </div>
          </div>
          {errorMessage && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200">
              {errorMessage}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">Cargando asignaciones...</div>
          ) : filteredAsignaciones.length === 0 ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              No hay asignaciones que coincidan con tu búsqueda.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead className="bg-[#307CAF] text-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th
                      onClick={() => handleSort('nombreUsuario')}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20"
                    >
                      <div className="flex items-center gap-1">
                        Usuario
                        {sortConfig?.key === 'nombreUsuario' && (
                          <span className="material-symbols-outlined text-sm">
                            {sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('nombreEmpresa')}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20"
                    >
                      <div className="flex items-center gap-1">
                        Empresa
                        {sortConfig?.key === 'nombreEmpresa' && (
                          <span className="material-symbols-outlined text-sm">
                            {sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('nombreRol')}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20"
                    >
                      <div className="flex items-center gap-1">
                        Rol
                        {sortConfig?.key === 'nombreRol' && (
                          <span className="material-symbols-outlined text-sm">
                            {sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filteredAsignaciones.map((asig) => (
                    <tr key={asig.usuarioEmpresaId} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-slate-900 dark:text-white">{asig.nombreUsuario}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">ID #{asig.usuarioId}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-800 dark:text-slate-100">{asig.nombreEmpresa}</p>
                        <p className="text-xs text-slate-400">Empresa #{asig.empresaId}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-primary/10 text-primary">
                          <span className="material-symbols-outlined text-sm">shield</span>
                          {asig.nombreRol}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openEditModal(asig)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                          >
                            <span className="material-symbols-outlined text-sm">edit</span>
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(asig)}
                            className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <span className="material-symbols-outlined text-sm">delete</span>
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div
            className={`relative w-full max-w-lg transform rounded-2xl bg-white dark:bg-slate-800 p-6 shadow-xl transition-all ${isModalClosing ? 'animate-[disminuir_0.3s_ease-in_forwards]' : 'animate-[crecer_0.3s_ease-out_forwards]'}`}
          >
            <div className="rounded-2xl backdrop-blur-sm bg-white/50 dark:bg-slate-800 p-6 shadow-xl dark:border-slate-700 max-h-[90vh] overflow-y-auto">
              <button
                onClick={closeModal}
                className="absolute right-3 top-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">
                {editingAsignacion ? 'Editar asignación' : 'Nueva asignación'}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                Selecciona el Usuario, Empresa y Rol que deseas vincular.
              </p>
              <form className="space-y-4" onSubmit={handleSubmit}>
                {isSuperAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Empresa</label>
                    <select
                      value={formData.empresaId}
                      onChange={(e) => setFormData((prev) => ({ ...prev, empresaId: Number(e.target.value) }))}
                      className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    >
                      <option value={0}>Seleccionar empresa...</option>
                      {empresas.filter(e => e.activa).map((empresa) => (
                        <option key={empresa.empresaId} value={empresa.empresaId}>
                          {empresa.nombreEmpresa}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Usuario</label>
                  <select
                    value={formData.usuarioId}
                    onChange={(e) => setFormData((prev) => ({ ...prev, usuarioId: Number(e.target.value) }))}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value={0}>Seleccionar usuario...</option>
                    {filteredUsuariosForEmpresa.map((usuario) => (
                      <option key={usuario.usuarioId} value={usuario.usuarioId}>
                        {usuario.nombreUsuario} ({usuario.telefono})
                      </option>
                    ))}
                  </select>
                  {formData.empresaId > 0 && filteredUsuariosForEmpresa.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">No hay usuarios activos en esta empresa.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Rol</label>
                  <select
                    value={formData.rolId}
                    onChange={(e) => setFormData((prev) => ({ ...prev, rolId: Number(e.target.value) }))}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value={0}>Seleccionar rol...</option>
                    {roles.filter(r => r.activo && (isSuperAdmin || r.nombreRol !== 'SuperAdmin')).map((rol) => (
                      <option key={rol.rolId} value={rol.rolId}>
                        {rol.nombreRol}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                    onClick={closeModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#307CAF] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary-hover disabled:opacity-60"
                  >
                    {isSaving ? (
                      <>
                        <span className="material-symbols-outlined animate-spin">progress_activity</span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined">save</span>
                        Guardar
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuarioEmpresas;
